import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { GenericEntity } from '../types';
import dayjs from 'dayjs';
import { EntityFields, EntityMetadata, FieldValue } from '../../UI/dynamicUI/types';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';

type UpsertEntityData = {
  entityType: string;
  fields: GenericEntity | GenericEntity[];
};

export type UpsertEntityInput = {
  entityFieldsMetadata: EntityMetadata;
  entityType: string;
  values: EntityFields | EntityFields[];
};

type EntityFieldsToFilterOut = { entityType: string; fieldNames: string[] };

type UpsertVariables = {
  clientId: string;
  values: UpsertEntityData[];
  deleteMissingWhenUpsertingList: boolean;
  transactional: boolean;
};

export function useUpsertEntity() {
  const queryClient = useQueryClient();
  const { flagEnabledBatchCrudDataUpload } = useFlags();
  const {
    mutateAsync,
    data,
    isError,
    isPending: isLoading,
    reset,
  } = useMutation<{ data: string[] }, Error, UpsertVariables>({
    mutationFn: ({ clientId, values, deleteMissingWhenUpsertingList, transactional }) =>
      api.put<string[]>(
        `clients/${clientId}/entities?delete_missing_when_upserting_list=${deleteMissingWhenUpsertingList}&transactional=${transactional}`,
        values,
      ),
  });
  const { refetch: refetchCurrentClient, isLoading: isLoadingClient } =
    useCurrentClientIfAvailable();

  const upsertEntities = async (
    clientId: string,
    upsertEntities: UpsertEntityInput[],
    deleteMissingWhenUpsertingList: boolean,
    entityFieldsToFilterOut?: EntityFieldsToFilterOut,
    transactional: boolean = true,
  ) => {
    const filteredUpsertEntities = upsertEntities.map(upsertInput =>
      entityFieldsToFilterOut ? filterOutFields(entityFieldsToFilterOut, upsertInput) : upsertInput,
    );
    const values: UpsertEntityData[] = filteredUpsertEntities.map(
      ({ entityFieldsMetadata, entityType, values }) => ({
        entityType: entityType,
        fields: Array.isArray(values)
          ? values.map(v => cleanUpFieldsForCreation(entityFieldsMetadata, v))
          : cleanUpFieldsForCreation(entityFieldsMetadata, values),
      }),
    );

    const result = await mutateAsync({
      clientId,
      values,
      deleteMissingWhenUpsertingList,
      transactional,
    });
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['clients', clientId],
        predicate: flagEnabledBatchCrudDataUpload
          ? query => !query.queryKey.includes('detections')
          : undefined,
      }),
      refetchCurrentClient(),
    ]);
    return result;
  };

  const upsertEntity = async (
    clientId: string,
    upsertEntity: UpsertEntityInput,
    deleteMissingWhenUpsertingList: boolean,
    entityFieldsToFilterOut?: EntityFieldsToFilterOut,
  ) => {
    const { data } = await upsertEntities(
      clientId,
      [upsertEntity],
      deleteMissingWhenUpsertingList,
      entityFieldsToFilterOut,
    );
    if (data && data.length === 1) {
      return data[0];
    }

    return null;
  };

  const filterOutFields = (
    entityFieldsToFilterOut: EntityFieldsToFilterOut,
    upsertInput: UpsertEntityInput,
  ) => {
    if (
      Array.isArray(upsertInput) ||
      upsertInput.entityType !== entityFieldsToFilterOut.entityType
    ) {
      return upsertInput;
    }

    const values = { ...upsertInput.values } as EntityFields;
    entityFieldsToFilterOut.fieldNames.forEach(fieldName => {
      if (!Array.isArray(upsertInput) && fieldName in values) {
        delete values[fieldName];
      }
    });
    return { ...upsertInput, values };
  };

  return {
    upsertEntities,
    upsertEntity,
    response: data?.data,
    isError,
    isLoading: isLoading || isLoadingClient,
    reset,
  };
}

type NonDateFieldValue = Exclude<FieldValue, Date>;

const cleanUpFieldsForCreation = (
  entityMetadata: EntityMetadata,
  values: EntityFields,
): GenericEntity => {
  try {
    const cleanedValues = {} as GenericEntity;
    Object.keys(values).forEach(fieldName => {
      try {
        const fieldValue = values[fieldName] as NonDateFieldValue | NonDateFieldValue[];
        if (fieldName === 'id') {
          cleanedValues[fieldName] = fieldValue;
          return;
        }
        // Owners (Phase 4) are forwarded as a resolved list with no detector field metadata;
        // pass them through untouched rather than looking up a metadata type that doesn't exist.
        if (fieldName === 'owners') {
          cleanedValues[fieldName] = fieldValue;
          return;
        }
        const fieldType = entityMetadata[fieldName].type;

        if (fieldValue === '') {
          cleanedValues[fieldName] = null;
          return;
        }

        if (!fieldValue) {
          cleanedValues[fieldName] = fieldValue;
          return;
        }
        if (fieldType === 'PERCENTAGE' || fieldValue === 'NUMBER') {
          cleanedValues[fieldName] = parseFloat(fieldValue.toString());
        } else if (fieldType === 'MONEY') {
          cleanedValues[fieldName] = parseFloat(fieldValue.toString());
        } else if (fieldType === 'DATE') {
          cleanedValues[fieldName] = dayjs(fieldValue as string).format('YYYY-MM-DD');
        } else if (fieldType === 'BOOLEAN') {
          cleanedValues[fieldName] = fieldValue === 'true' || fieldValue === true;
        } else if (fieldType === 'SELECT') {
          if (fieldValue === 'Yes') {
            cleanedValues[fieldName] = true;
          } else if (fieldValue === 'No') {
            cleanedValues[fieldName] = false;
          } else {
            cleanedValues[fieldName] = fieldValue;
          }
        } else if (fieldType === 'LIST') {
          if (Array.isArray(fieldValue)) {
            cleanedValues[fieldName] = fieldValue
              ?.filter(x => x !== '')
              .map(item => {
                // naively clean up date objects
                if (typeof item === 'object' && item !== null) {
                  return Object.fromEntries(
                    Object.entries(item).map(([k, v]) => [
                      k,
                      v instanceof Date ? dayjs(v).format('YYYY-MM-DD') : v,
                    ]),
                  ) as GenericEntity;
                }
                return item as NonDateFieldValue;
              }) as NonDateFieldValue[] | GenericEntity[];
          }
        } else if (typeof fieldValue === 'string') {
          const trimmed = fieldValue.trim();
          cleanedValues[fieldName] = trimmed === '' ? null : trimmed;
        } else {
          cleanedValues[fieldName] = fieldValue;
        }
      } catch (e) {
        if (e instanceof Error) {
          e.message = e.message + ` for field ${fieldName}`;
        }
        throw e;
      }
    });

    return cleanedValues;
  } catch (e) {
    if (e instanceof Error) {
      e.message = e.message + ` for entity type ${entityMetadata.entityType}`;
    }
    throw e;
  }
};
